/**
 * A stale tab asking for a chunk that no longer exists, answered with one reload.
 *
 * Every deploy writes new hashed filenames and drops the old ones. A tab opened before it
 * still holds the previous `index.html` in memory, so the first lazy route it navigates to
 * asks for `assets/index-<old hash>.js`, gets a 404 and throws
 * `Failed to fetch dynamically imported module` — the screen simply does not change, and
 * nothing tells the user why. Vite raises `vite:preloadError` for exactly this, and the
 * only useful answer is to fetch the new `index.html`: `location.reload()`.
 *
 * **Once, not in a loop.** The same error also happens with no deploy behind it — a dropped
 * connection, a proxy, an ad blocker eating a request — and there a reload fixes nothing.
 * A timestamp in `sessionStorage` bounds it: one reload per tab per `RETRY_WINDOW_MS`, and
 * the second failure inside that window is left to throw, which is what puts it in Sentry.
 *
 * **In `src/support`, not `src/shared/lib`, and that placement is load-bearing.** The two
 * app entries are the only callers, and anything they import from `src/shared` drags the
 * whole `shared` manual chunk (`vite.config.js`) into the static graph — measured: importing
 * this one function from `@/shared/lib` moved 1.15 MB of traforama's lazily-loaded shared
 * code into its first page load. It is bootstrap infrastructure, like `support/env`, not a
 * helper an FSD slice reaches for.
 *
 * `sessionStorage` rather than a module variable, because the reload restarts the module.
 * It is read through `try`/`catch`: a browser with site data blocked throws on access, and
 * a page that will not load is not the place to add a second reason to fail.
 */
const LAST_RELOAD_KEY = 'chunk-reload-at'

/** Long enough to cover a deploy's propagation, short enough not to strand a session. */
const RETRY_WINDOW_MS = 10_000

function reloadedRecently(): boolean {
  try {
    const at = Number(sessionStorage.getItem(LAST_RELOAD_KEY) ?? 0)

    return Number.isFinite(at) && Date.now() - at < RETRY_WINDOW_MS
  } catch {
    // No session storage: allow the reload. Once is still better than never, and the
    // worst case is a second one the user can interrupt.
    return false
  }
}

function markReloaded(): void {
  try {
    sessionStorage.setItem(LAST_RELOAD_KEY, String(Date.now()))
  } catch {
    // Nothing to do — see above.
  }
}

export function reloadOnChunkError(): void {
  window.addEventListener('vite:preloadError', (event) => {
    if (reloadedRecently()) return

    // Vite throws the original error unless this is prevented; we are handling it.
    event.preventDefault()
    markReloaded()
    window.location.reload()
  })
}
